import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  forwardRef,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { createPopper, Instance } from '@popperjs/core';
import { LoadingShimmerComponent } from '../../../loading-shimmer/loading-shimmer.component';
import { InputSize } from '../../../enum/input-size.enum';

@Component({
  selector: 'lib-typeahead-input',
  templateUrl: './typeahead-input.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TypeaheadInputComponent),
      multi: true,
    },
  ],
  imports: [MatIcon, LoadingShimmerComponent],
  styleUrl: './typeahead-input.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TypeaheadInputComponent implements ControlValueAccessor {
  private readonly defaultPlaceholder = $localize`Search...`;
  placeholder = input<string, string | null | undefined>(this.defaultPlaceholder, {
    transform: (value: string | null | undefined) => value ?? this.defaultPlaceholder,
  });
  controlId = input<string | null>(null);
  error = input<boolean>(false);
  inputSize = input<InputSize>(InputSize.large);
  showSelectedValue = input<boolean>(true);
  results = input<string[] | null>(null);
  loading = input<boolean>(false);
  resultsMatchesInputWidth = input<boolean>(true);
  initialSelectedName = input<string | null>();
  valueChange = output<string | null>();
  resultIndexSelected = output<number>();

  typeaheadInput = viewChild<ElementRef<HTMLDivElement>>('typeaheadInput');
  typeaheadResults = viewChild<ElementRef<HTMLDivElement>>('typeaheadResults');

  private popperInstance: Instance | undefined;

  private readonly resultsContentEffect = effect(() => {
    if (!this.isFocussed()) {
      return;
    }

    const shouldOpenResults = (this.loading() || this.results() !== null) && !this.showResultsContent();
    const shouldCloseResults = !this.loading() && this.results() === null && this.showResultsContent();
    if (shouldOpenResults) {
      this.openResults();
    } else if (shouldCloseResults) {
      this.closeResults();
    }
  });

  private readonly initialValueEffect = effect(() => {
    const initialSelectedName = this.initialSelectedName();
    if (initialSelectedName) {
      this.currentSelectedResult.set(initialSelectedName);
      this.value.set(initialSelectedName);
      this.typeaheadInput()?.nativeElement.blur();
    } else {
      this.currentSelectedResult.set(null);
      this.value.set(null);
    }
  });

  protected readonly InputSize = InputSize;

  public value = signal<string | null>(null);
  public isFocussed = signal<boolean>(false);
  public disabled = signal(false);
  public clearButtonVisible = computed(() => {
    const value = this.value();
    return value !== null && value.length > 0;
  });
  showResultsContent = signal(false);
  currentSelectedResult = signal<string | null | undefined>(null);

  public setDisabledState?(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  public writeValue(value: string | null): void {
    this.value.set(value);
  }

  public registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  onFocus() {
    this.isFocussed.set(true);
  }

  onBlur() {
    this.isFocussed.set(false);
    this.closeResults();
  }

  public handleInput(event: Event): void {
    const inputValue = (event.target as HTMLInputElement).value;
    this.value.set(inputValue);
    this.valueChange.emit(inputValue);
    this.onChange(inputValue);
  }

  public clear(): void {
    if (this.disabled()) {
      return;
    }
    this.value.set(null);
    this.valueChange.emit(null);
    this.onChange(null);
    this.currentSelectedResult.set(null);
  }

  public openResults(): void {
    if (!this.disabled()) {
      this.showResultsContent.set(true);
      this.positionDropdown();
    }
  }

  public closeResults(): void {
    setTimeout(() => {
      this.showResultsContent.set(false);
      this.popperInstance?.destroy();
    });
  }

  resultSelected(resultIndex: number): void {
    this.resultIndexSelected.emit(resultIndex);
    if (this.showSelectedValue()) {
      const result = this.results()?.[resultIndex];
      this.currentSelectedResult.set(result ?? null);
      this.value.set(result ?? null);
    } else {
      this.clear();
    }
    this.typeaheadInput()?.nativeElement.blur();
  }

  private onChange: (value: string | null) => void = () => {
    // default onChange
  };
  private onTouched: () => void = () => {
    // default onTouched
  };

  private positionDropdown(): void {
    const typeaheadResults = this.typeaheadResults();
    const typeaheadInput = this.typeaheadInput();
    if (!typeaheadResults || !typeaheadInput) return;
    const dropdownElement = typeaheadResults.nativeElement;
    this.popperInstance = createPopper(typeaheadInput.nativeElement, dropdownElement, {
      placement: 'bottom-start',
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [0, 8],
          },
        },
        {
          name: 'eventListeners',
          enabled: true,
          options: {
            scroll: true,
          },
        },
        {
          name: 'flip',
          options: {
            altBoundary: true,
            padding: { bottom: 24 },
          },
        },
      ],
      strategy: 'fixed',
    });
    if (this.resultsMatchesInputWidth()) {
      dropdownElement.style.width = `${typeaheadInput.nativeElement.clientWidth}px`;
    }
  }
}
